import { useState } from "react";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowUpRight, RotateCw, Sparkles } from "lucide-react";

export function CopyCycleDialog({ open, onOpenChange, currentCiclo, grupos, onDone }) {
  const [nuevoCiclo, setNuevoCiclo] = useState(nextCiclo(currentCiclo));
  const [modo, setModo] = useState("subir_grado");
  const [nuevoGrado, setNuevoGrado] = useState("1");
  const [nuevaLetra, setNuevaLetra] = useState("A");
  const [mismoLetra, setMismoLetra] = useState("");
  const [running, setRunning] = useState(false);

  function nextCiclo(cur) {
    const m = /^(\d{4})-(\d{4})$/.exec(cur || "");
    if (!m) return "2026-2027";
    return `${parseInt(m[1]) + 1}-${parseInt(m[2]) + 1}`;
  }

  const submit = async (e) => {
    e.preventDefault();
    setRunning(true);
    try {
      const payload = {
        nuevo_ciclo: nuevoCiclo,
        modo,
        ciclo_actual: currentCiclo || null,
      };
      if (modo === "nuevo_grupo") {
        payload.nuevo_grado = parseInt(nuevoGrado, 10);
        payload.nueva_letra = nuevaLetra.toUpperCase();
      } else if (modo === "mismo_grado" && mismoLetra.trim()) {
        payload.nueva_letra = mismoLetra.toUpperCase().trim();
      }
      const { data } = await api.post("/grupos/copiar-ciclo", payload);
      if (modo === "nuevo_grupo") {
        toast.success(`Nuevo grupo ${payload.nuevo_grado}°${payload.nueva_letra} creado para ciclo ${nuevoCiclo}`);
      } else {
        toast.success(
          `Ciclo ${nuevoCiclo} creado — ${data.grupos_creados} grupo(s), ${data.alumnos_copiados} alumno(s), ${data.egresados} egresado(s)`,
        );
      }
      onOpenChange(false);
      onDone?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setRunning(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Iniciar nuevo ciclo escolar</DialogTitle>
          <DialogDescription>
            Selecciona cómo continuar el trabajo con tus grupos en el ciclo <strong>{nuevoCiclo}</strong>.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs">Nuevo ciclo escolar *</Label>
            <Input
              data-testid="copy-nuevo-ciclo"
              value={nuevoCiclo}
              onChange={(e) => setNuevoCiclo(e.target.value)}
              placeholder="2026-2027"
              pattern="\d{4}-\d{4}"
              required
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs">¿Qué hará el docente en el nuevo ciclo?</Label>
            <RadioGroup value={modo} onValueChange={setModo} className="space-y-2">
              <label className="flex items-start gap-3 p-3 border rounded border-[#E5E7EB] hover:border-[#235B4E] cursor-pointer">
                <RadioGroupItem value="subir_grado" id="mode-subir" data-testid="mode-subir" />
                <div>
                  <div className="flex items-center gap-1 text-sm font-medium">
                    <ArrowUpRight className="w-4 h-4 text-[#235B4E]" /> Ascender un grado (1°A → 2°A)
                  </div>
                  <div className="text-xs text-[#545454] mt-0.5">
                    El docente acompaña al mismo grupo al siguiente grado. Los alumnos activos se copian.
                  </div>
                </div>
              </label>
              <label className="flex items-start gap-3 p-3 border rounded border-[#E5E7EB] hover:border-[#235B4E] cursor-pointer">
                <RadioGroupItem value="mismo_grado" id="mode-mismo" data-testid="mode-mismo" />
                <div className="flex-1">
                  <div className="flex items-center gap-1 text-sm font-medium">
                    <RotateCw className="w-4 h-4 text-[#BC955C]" /> Quedarse en el mismo grado (puede cambiar de grupo)
                  </div>
                  <div className="text-xs text-[#545454] mt-0.5">
                    El docente recibe un grupo del mismo grado. Los alumnos anteriores pasan a otro docente. Puede mantener la misma letra o elegir una diferente.
                  </div>
                  {modo === "mismo_grado" && (
                    <div className="mt-3 grid grid-cols-2 gap-2 max-w-xs">
                      <div className="col-span-2">
                        <Label className="text-[10px] uppercase tracking-wider text-[#6B7280]">Letra / grupo (opcional)</Label>
                        <Input
                          data-testid="mismo-grado-letra"
                          value={mismoLetra}
                          onChange={(e) => setMismoLetra(e.target.value.toUpperCase())}
                          maxLength={3}
                          className="uppercase mt-1"
                          placeholder="Dejar vacío para conservar la letra actual"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </label>
              <label className="flex items-start gap-3 p-3 border rounded border-[#E5E7EB] hover:border-[#235B4E] cursor-pointer">
                <RadioGroupItem value="nuevo_grupo" id="mode-nuevo-grupo" data-testid="mode-nuevo-grupo" />
                <div className="flex-1">
                  <div className="flex items-center gap-1 text-sm font-medium">
                    <Sparkles className="w-4 h-4 text-[#9F2241]" /> Nuevo grado
                  </div>
                  <div className="text-xs text-[#545454] mt-0.5">
                    Al docente se le asigna un grupo nuevo con el grado y letra que elijas. No se copian alumnos. Este es asignado por el director.
                  </div>
                  {modo === "nuevo_grupo" && (
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-[10px] uppercase tracking-wider text-[#6B7280]">Grado</Label>
                        <Select value={nuevoGrado} onValueChange={setNuevoGrado}>
                          <SelectTrigger data-testid="nuevo-grupo-grado" className="mt-1"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {[1, 2, 3, 4, 5, 6].map((g) => (
                              <SelectItem key={g} value={String(g)}>{g}°</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase tracking-wider text-[#6B7280]">Letra / grupo</Label>
                        <Input
                          data-testid="nuevo-grupo-letra"
                          value={nuevaLetra}
                          onChange={(e) => setNuevaLetra(e.target.value.toUpperCase())}
                          maxLength={3}
                          className="uppercase mt-1"
                          placeholder="A"
                          required
                        />
                      </div>
                    </div>
                  )}
                </div>
              </label>
            </RadioGroup>
          </div>
          {modo !== "nuevo_grupo" && (
            <div className="text-xs text-[#545454] p-3 bg-[#F9FAFB] rounded border">
              Se procesarán <strong>{grupos.length}</strong> grupo(s) del ciclo actual.
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button data-testid="btn-copy-confirm" type="submit" disabled={running} className="bg-[#235B4E] hover:bg-[#1D4A40]">
              {running ? "Procesando…" : modo === "nuevo_grupo" ? "Crear grupo" : "Copiar ciclo"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
